import { useTranslation } from 'react-i18next';
import { MoneyInput } from '../money-input.tsx';
import { useSession } from '../../state/session.tsx';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';

/**
 * The billing half of a series form: what a visit costs. Shared by "new
 * schedule" and the series editor.
 *
 * WHETHER THE CARD IS CHARGED IS NO LONGER ASKED HERE. Two settings decided
 * that and either was enough — a per-schedule "Manual or Automatic" dropdown
 * set at booking, and the account-wide "charge the card on file" switch in
 * Settings → Payments. `autoBillOnCompletion` ORs them, so a company with the
 * account switch on got automatic billing whatever this dropdown said, and a
 * company with it off had to remember the dropdown on every schedule forever
 * — a decision made once, then asked again on every booking, where the answer
 * that matters is already on file.
 *
 * So the account switch is the setting, and this form has one question again:
 * the price. The `billing_mode` column stays, `autoBillOnCompletion` still
 * reads it, and a schedule already set to Automatic goes on charging exactly
 * as it does today — which is the whole reason the column was kept rather
 * than migrated away. Those schedules say so on their own page; nothing new
 * can be created in that state.
 */
export interface BillingFormState {
  visitPriceCents: number | null;
}

export function BillingFields({
  billing,
  onChange,
  fieldErrors = {},
  disabled,
  priceHint,
}: {
  billing: BillingFormState;
  onChange: (next: BillingFormState) => void;
  fieldErrors?: Record<string, string>;
  disabled?: boolean;
  /**
   * Replaces the standing hint under the price. The booking form uses it to
   * say where a suggested price came from — a figure that appears by itself
   * has to explain itself, or the reader cannot tell whether it is a
   * suggestion to check or a decision already made.
   */
  priceHint?: string;
}) {
  const { t } = useTranslation();
  const { currencyCode } = useSession();
  return (
    <div className="grid gap-x-4 sm:grid-cols-2">
      <MoneyInput
        label={t('billingFields.pricePerVisit')}
        hint={priceHint ?? t('billingFields.pricePerVisitHint')}
        valueCents={billing.visitPriceCents}
        currencyCode={currencyCode}
        disabled={disabled ?? false}
        {...(fieldErrors.visitPriceCents ? { error: fieldErrors.visitPriceCents } : {})}
        onChangeCents={(cents) => onChange({ ...billing, visitPriceCents: cents })}
      />
    </div>
  );
}
